/**
 * EntityPicker — the search-and-pick combobox CORE (§35 item 8), extracted
 * from AccountPicker so the three id-valued pickers (account, contact, deal)
 * share one control: open/close, the 200 ms debounced server search while
 * the panel is open, click-away, the combobox/listbox roles, Escape, the
 * "Searching…" line and the two empty-panel sentences. It owns NO value
 * contract: the consumer owns the text and decides what typing and picking
 * mean — for AccountPicker the text IS the company and a keystroke clears
 * the link; for a filter picker the text is display only and the id is what
 * travels. Proven by its consumers' tests, not on its own.
 *
 * Visual is AccountPicker's, unchanged (docs/design/stier/ADOPTION.md): the
 * input keeps the control radius (§8.4 REFUSED list — text inputs never
 * capsule); the results panel is a .k-card under the input; rows are plain
 * hover rows. No new tokens.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'

export interface EntityPickerProps<T> {
  id?: string
  className: string
  placeholder: string
  autoFocus?: boolean
  onBlur?: () => void
  /** The input's text — the consumer's state, whatever it means there. */
  text: string
  /** Every keystroke; the panel opens with it. */
  onTextChange: (text: string) => void
  /** The server search, called debounced (200 ms) while the panel is open. */
  search: (q: string) => Promise<T[]>
  rowKey: (row: T) => string
  renderRow: (row: T) => ReactNode
  /** A pick: the consumer takes the row, then the panel closes. */
  onPick: (row: T) => void
  /** The picked row's key, for aria-selected. */
  selectedKey?: string
  /** The empty panel: nothing typed yet / nothing matched. */
  emptyPrompt: string
  noMatch: string
  /** Marks laid over the input (a link state, a search hint). */
  adornment?: ReactNode
  /** One row under the results, the consumer's own (the create row). */
  footer?: (ctx: { rows: T[]; trimmed: string; close: () => void }) => ReactNode
}

export function EntityPicker<T>({ id, className, placeholder, autoFocus, onBlur, text, onTextChange, search, rowKey, renderRow, onPick, selectedKey, emptyPrompt, noMatch, adornment, footer }: EntityPickerProps<T>) {
  const [open, setOpen] = useState(false)
  const [rows, setRows] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  // The search function is read through a ref so a consumer's inline arrow
  // does not restart the debounce on every parent render; the ref is
  // refreshed in an effect (never during render) so the timer below always
  // calls the latest one.
  const searchRef = useRef(search)
  useEffect(() => { searchRef.current = search }, [search])

  // Debounced server search while the panel is open.
  useEffect(() => {
    if (!open) return
    let alive = true
    const t = setTimeout(() => {
      setLoading(true)
      searchRef.current(text)
        .then(r => { if (alive) setRows(r) })
        .catch(() => { if (alive) setRows([]) })
        .finally(() => { if (alive) setLoading(false) })
    }, 200)
    return () => { alive = false; clearTimeout(t) }
  }, [open, text])

  // Click-away closes the panel.
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const trimmed = text.trim()
  const close = () => setOpen(false)
  const listboxId = id ? `${id}-listbox` : undefined

  return (
    <div ref={wrap} className="relative">
      <input id={id} type="text" value={text} autoFocus={autoFocus} autoComplete="off"
        role="combobox" aria-expanded={open} aria-autocomplete="list" aria-controls={listboxId}
        onChange={e => { onTextChange(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onBlur={onBlur}
        onKeyDown={e => { if (e.key === 'Escape') setOpen(false) }}
        placeholder={placeholder} className={className} />
      {adornment}
      {open && (
        <div id={listboxId} role="listbox"
          className="k-card absolute left-0 right-0 z-20 mt-1 overflow-hidden">
          {loading && rows.length === 0 && (
            <div className="px-4 py-3 text-12 text-ink-muted">Searching…</div>
          )}
          {rows.map(r => {
            const key = rowKey(r)
            return (
              <button key={key} type="button" role="option" aria-selected={key === selectedKey}
                onClick={() => { onPick(r); setOpen(false) }}
                className="w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-surface-row-hover transition-colors">
                {renderRow(r)}
              </button>
            )
          })}
          {!loading && rows.length === 0 && (
            <div className="px-4 py-3 text-12 text-ink-muted">
              {trimmed === '' ? emptyPrompt : noMatch}
            </div>
          )}
          {footer?.({ rows, trimmed, close })}
        </div>
      )}
    </div>
  )
}
